import React from 'react';
import { useTranslation } from 'react-i18next';

const BUCKET_STYLES = {
    today:    { color: '#059669', bg: '#ecfdf5', label: 'Today' },
    '1d':     { color: '#0891b2', bg: '#ecfeff', label: '1–2d' },
    '2-7d':   { color: '#d97706', bg: '#fffbeb', label: '2–7d' },
    over_7d:  { color: '#ef4444', bg: '#fef2f2', label: '>7d' },
    default:  { color: '#6b7280', bg: '#f3f4f6', label: '—' },
};

const AgingBadge = ({ bucket }) => {
    const s = BUCKET_STYLES[bucket] || BUCKET_STYLES.default;
    return (
        <span style={{
            fontSize: 11, fontWeight: 700,
            color: s.color, backgroundColor: s.bg,
            borderRadius: 12, padding: '3px 9px', whiteSpace: 'nowrap',
        }}>
            {s.label}
        </span>
    );
};

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');

const formatDateTime = (d) => {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
        });
    } catch {
        return String(d);
    }
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

const PendingBacklogTable = ({ items = [], loading }) => {
    const { t } = useTranslation();
    return (
        <div style={cardStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('common.analyticsWidgets.pendingBacklog.title')}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('common.analyticsWidgets.pendingBacklog.subtitle')}</div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', backgroundColor: '#f3f4f6', borderRadius: 20, padding: '3px 10px' }}>
                        {items.length} records
                    </span>
                )}
            </div>

            {loading && <EmptyState message="Loading backlog..." />}
            {!loading && !items.length && <EmptyState message="No pending sub-orders for the selected filters." />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1040 }}>
                        <thead>
                            <tr>
                                <Th>#</Th>
                                <Th>{t('common.analyticsColumns.subOrder')}</Th>
                                <Th>{t('common.analyticsColumns.customer')}</Th>
                                <Th>{t('common.analyticsColumns.warehouse')}</Th>
                                <Th>{t('common.analyticsColumns.supervisor')}</Th>
                                <Th>{t('common.analyticsColumns.status')}</Th>
                                <Th>{t('common.analyticsColumns.vehicle')}</Th>
                                <Th align="right">{t('common.analyticsColumns.weight')}</Th>
                                <Th>{t('common.analyticsColumns.assigned')}</Th>
                                <Th align="right">{t('common.analyticsColumns.pending')}</Th>
                                <Th align="center">{t('common.analyticsColumns.aging')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => (
                                <tr
                                    key={row.sub_order_id ?? i}
                                    style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                >
                                    <Cell muted>{i + 1}</Cell>
                                    <Cell>
                                        <span style={{ fontWeight: 700, color: '#0891b2' }}>
                                            {row.sub_order_number ?? row.sub_order_id ?? '—'}
                                        </span>
                                        {row.is_direct_flow && (
                                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#7c3aed', backgroundColor: '#f5f3ff', borderRadius: 8, padding: '1px 6px' }}>
                                                DIRECT
                                            </span>
                                        )}
                                    </Cell>
                                    <Cell>{row.customer_name}</Cell>
                                    <Cell muted>{row.warehouse_name}</Cell>
                                    <Cell muted>{row.supervisor_name}</Cell>
                                    <Cell muted style={{ textTransform: 'capitalize' }}>{row.status?.replace(/_/g, ' ')}</Cell>
                                    <Cell muted>{row.vehicle_number}</Cell>
                                    <Cell align="right">{row.total_weight_kg != null ? `${fmt(row.total_weight_kg)} kg` : '—'}</Cell>
                                    <Cell muted>{formatDateTime(row.assigned_at)}</Cell>
                                    <Cell align="right">
                                        <span style={{ fontWeight: 700, color: '#111827' }}>{row.pending_human ?? '—'}</span>
                                    </Cell>
                                    <Cell align="center"><AgingBadge bucket={row.aging_bucket} /></Cell>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PendingBacklogTable;
